
import Vue from "vue";
import { mapActions } from "vuex";
import { POST_QUESTIONS } from "@/store/modules/recruiter/constants";
import { parseStrToInt } from "@/utils/global";

interface Question {
  question_text: string;
  question_type: string;
  question_options: {
    order: number;
  };
}

interface Snackbar {
  show: boolean;
  message: string;
  color: string;
}

export default Vue.extend({
  name: "AddQuestionsToStandardisedJob",
  data() {
    return {
      valid: false,
      showQuestionInput: false,
      currentQuestion: "",
      questions: [] as Question[],
      snackbar: {
        show: false,
        message: "",
        color: "success"
      } as Snackbar,
      questionRules: [
        (v: string) => !!v.trim() || "Question is required",
        (v: string) =>
          v.trim().length >= 5 || "Question must be at least 5 characters long"
      ]
    };
  },
  computed: {
    formatQuestionsArray(): string {
      return JSON.stringify(this.questions, null, 2);
    }
  },
  methods: {
    addQuestionInput(): void {
      this.showQuestionInput = true;
      this.currentQuestion = "";
      // Focus on input field after showing it
      this.$nextTick(() => {
        const input = this.$el.querySelector(
          'input[type="text"]'
        ) as HTMLInputElement;
        if (input) {
          input.focus();
        }
      });
    },

    saveQuestion(): void {
      if (this.currentQuestion.trim()) {
        const newQuestion: Question = {
          question_text: this.currentQuestion.trim(),
          question_type: "text",
          question_options: {
            order: this.questions.length + 1
          }
        };

        this.questions.push(newQuestion);
        this.currentQuestion = "";
        this.showQuestionInput = false;

        this.showSnackbar("Question added successfully!", "success");
      }
    },

    cancelQuestion(): void {
      this.currentQuestion = "";
      this.showQuestionInput = false;
    },

    removeQuestion(index: number): void {
      this.questions.splice(index, 1);

      // Reorder remaining questions
      this.questions.forEach((question, idx) => {
        question.question_options.order = idx + 1;
      });

      this.showSnackbar("Question removed successfully!", "info");
    },

    clearAllQuestions(): void {
      this.questions = [];
      this.showSnackbar("All questions cleared!", "warning");
    },

    async submitQuestions(): Promise<void> {
      if (this.questions.length > 0) {
        try {
          // Get assessment_id and job_id from route params
          const assessment_id = 11; // Default to 1 if not found
          const job_id = parseStrToInt(this.$route.params.id) || 1; // Default to 1 if not found

          // Prepare payload
          const payload = {
            assessment_id,
            job_id,
            questions: this.questions
          };

          // Call the API
          const response = await this.post_questions(payload);

          if (response) {
            console.log("Questions submitted successfully:", response);
            this.showSnackbar(
              `${this.questions.length} questions submitted successfully!`,
              "success"
            );
            this.$router.push({
              name: "view-standardised-job",
              params: { id: this.$route.params.id }
            });
          } else {
            this.showSnackbar(
              "Failed to submit questions. Please try again.",
              "error"
            );
          }
        } catch (error) {
          console.error("Error submitting questions:", error);
          this.showSnackbar(
            "An error occurred while submitting questions.",
            "error"
          );
        }
      }
    },

    async copyToClipboard(): Promise<void> {
      try {
        await navigator.clipboard.writeText(this.formatQuestionsArray);
        this.showSnackbar("JSON copied to clipboard!", "success");
      } catch (err) {
        this.showSnackbar("Failed to copy to clipboard", "error");
      }
    },

    showSnackbar(message: string, color: string): void {
      this.snackbar = {
        show: true,
        message,
        color
      };
    },

    // Vuex actions
    ...mapActions("recruiter", {
      post_questions: POST_QUESTIONS
    })
  }
});
